import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SidebarPanel from '../../components/admin/SidebarPanel';
import PipelineRunsPanel from './PipelineRunsPanel';
import {
  fetchCases,
  loadCase,
  fetchProviders,
  fetchTemplates,
  fetchAvailableModels,
  fetchLLMPromptTemplates,
  generateNote,
  convertNoteFormats,
  setSelectedProvider,
  setSelectedTemplate,
  setSelectedLLMPromptTemplate,
  updateCustomLLMPrompt,
  resetCustomLLMPrompt,
  updateCustomNoteTemplateContent,
  updateProviderPreferences,
  resetProviderPreferences,
  updatePriorNotesConfig,
  addPriorNoteSection,
  removePriorNoteSection,
  clearPriorNotes,
  setEditedTranscript,
  resetTranscript,
  setCustomInstructions,
  updateGenerationOptions,
  resetSandbox,
  selectAvailableCases,
  selectSelectedCase,
  selectTranscript,
  selectTranscriptEdited,
  selectExistingNotes,
  selectAvailableProviders,
  selectAvailableTemplates,
  selectAvailableModels,
  selectAvailableLLMPromptTemplates,
  selectModelsStatus,
  selectLLMPromptTemplatesStatus,
  selectSelectedProviderId,
  selectSelectedTemplateId,
  selectCustomNoteTemplateContent,
  selectCustomInstructions,
  selectSelectedLLMPromptTemplateId,
  selectCustomLLMPrompt,
  selectProviderPreferencesOverride,
  selectPriorNotesConfig,
  selectGeneratedNote,
  selectGeneratedFormats,
  selectGenerationOptions,
  selectGenerateStatus,
  selectGenerateError,
  selectConvertStatus,
  selectConvertError,
  selectFinalUserMessage,
  selectFinalSystemMessage
} from '../../components/redux/slices/noteGenerationSandboxSlice';

/**
 * Note Generation Sandbox - Mix & Match Testing Tool
 *
 * Purpose:
 * - Load existing cases from the database
 * - Override any component (provider, template, transcript)
 * - Test different combinations
 * - Compare results with existing notes
 */
const NoteGenerationSandbox = () => {
  const dispatch = useDispatch();

  // Redux state
  const availableCases = useSelector(selectAvailableCases);
  const selectedCase = useSelector(selectSelectedCase);
  const transcript = useSelector(selectTranscript);
  const transcriptEdited = useSelector(selectTranscriptEdited);
  const existingNotes = useSelector(selectExistingNotes);
  const availableProviders = useSelector(selectAvailableProviders);
  const availableTemplates = useSelector(selectAvailableTemplates);
  const availableModels = useSelector(selectAvailableModels);
  const availableLLMPromptTemplates = useSelector(selectAvailableLLMPromptTemplates);
  const modelsStatus = useSelector(selectModelsStatus);
  const llmPromptTemplatesStatus = useSelector(selectLLMPromptTemplatesStatus);
  const selectedProviderId = useSelector(selectSelectedProviderId);
  const selectedTemplateId = useSelector(selectSelectedTemplateId);
  const customNoteTemplateContent = useSelector(selectCustomNoteTemplateContent);
  const customInstructions = useSelector(selectCustomInstructions);
  const selectedLLMPromptTemplateId = useSelector(selectSelectedLLMPromptTemplateId);
  const customLLMPrompt = useSelector(selectCustomLLMPrompt);
  const providerPreferencesOverride = useSelector(selectProviderPreferencesOverride);
  const priorNotesConfig = useSelector(selectPriorNotesConfig);
  const generatedNote = useSelector(selectGeneratedNote);
  const generatedFormats = useSelector(selectGeneratedFormats);
  const generationOptions = useSelector(selectGenerationOptions);
  const generateStatus = useSelector(selectGenerateStatus);
  const generateError = useSelector(selectGenerateError);
  const convertStatus = useSelector(selectConvertStatus);
  const convertError = useSelector(selectConvertError);
  const finalUserMessage = useSelector(selectFinalUserMessage);
  const finalSystemMessage = useSelector(selectFinalSystemMessage);

  // Local UI state
  const [showPreferences, setShowPreferences] = useState(false);
  const [showPriorNotes, setShowPriorNotes] = useState(false);
  const [showLLMPromptEditor, setShowLLMPromptEditor] = useState(false);
  const [activeTab, setActiveTabLocal] = useState('json');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHealthSystem, setSelectedHealthSystem] = useState('');
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }

  // Searchable dropdown states
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [llmPromptSearchTerm, setLLMPromptSearchTerm] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showNoteTemplateEditor, setShowNoteTemplateEditor] = useState(false);
  const [showLLMPromptDropdown, setShowLLMPromptDropdown] = useState(false);
  const [showArchitecture, setShowArchitecture] = useState(false);

  // Refs for click-outside detection
  const providerDropdownRef = useRef(null);
  const templateDropdownRef = useRef(null);
  const llmPromptDropdownRef = useRef(null);

  // Copy to clipboard functionality
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      alert('Failed to copy to clipboard');
    }
  };

  // Pipeline runs sidebar
  const [showPipelineRunsPanel, setShowPipelineRunsPanel] = useState(false);

  const handleLoadPipelineRun = async (run) => {
    if (!run.input_data || !run.output_data) return alert('Invalid run data');
    try {
      const { input_data, output_data } = run;

      // Load the case if case_id exists
      if (run.case_id) {
        const result = await dispatch(loadCase(run.case_id));
        if (result.error) {
          console.warn('Failed to load case:', result.error);
        }
      }

      if (input_data.transcript) dispatch(setEditedTranscript(input_data.transcript));
      if (run.provider_id) dispatch(setSelectedProvider(run.provider_id));
      if (run.template_id) dispatch(setSelectedTemplate(run.template_id));
      if (run.llm_prompt_template_id) dispatch(setSelectedLLMPromptTemplate(run.llm_prompt_template_id));
      if (input_data.custom_instructions != null) {
        dispatch(setCustomInstructions(input_data.custom_instructions || ''));
      }
      if (input_data.provider_preferences_override) dispatch(updateProviderPreferences(input_data.provider_preferences_override));
      if (input_data.generation_options) dispatch(updateGenerationOptions(input_data.generation_options));
      if (input_data.prior_notes_data?.length) dispatch(updatePriorNotesConfig({ enabled: true, sections: input_data.prior_notes_data }));
      if (output_data.system_message) dispatch(updateCustomLLMPrompt({ system_message: output_data.system_message, isCustomized: false }));
      if (output_data.note_sections) {
        dispatch({ type: 'noteGenerationSandbox/generateNote/fulfilled', payload: output_data });
        dispatch(convertNoteFormats({ noteSections: output_data.note_sections, contentType: 'all' }));
      }
      setShowPipelineRunsPanel(false);
      // alert(`Loaded: ${run.name}`);
    } catch (error) {
      alert('Failed to load run: ' + error.message);
    }
  };

  // Click-outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (providerDropdownRef.current && !providerDropdownRef.current.contains(event.target)) {
        setShowProviderDropdown(false);
      }
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target)) {
        setShowTemplateDropdown(false);
      }
      if (llmPromptDropdownRef.current && !llmPromptDropdownRef.current.contains(event.target)) {
        setShowLLMPromptDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load initial data
  useEffect(() => {
    dispatch(fetchCases({ limit: 100 }));
    dispatch(fetchProviders());
    dispatch(fetchTemplates());
    dispatch(fetchAvailableModels());
    dispatch(fetchLLMPromptTemplates());
  }, [dispatch]);

  // Handle case selection
  const handleSelectCase = (caseItem) => {
    dispatch(loadCase(caseItem.id)); // Use database ID, not CoreServer_CaseID
  };

  // Handle transcript edit
  const handleTranscriptChange = (e) => {
    dispatch(setEditedTranscript(e.target.value));
  };

  // Handle transcript reset
  const handleResetTranscript = () => {
    dispatch(resetTranscript());
  };

  // Handle custom instructions (single source: Redux)
  const handleCustomInstructionsChange = (e) => {
    dispatch(setCustomInstructions(e.target.value));
  };

  // Handle generation options
  const handleGenerationOptionChange = (field, value) => {
    dispatch(updateGenerationOptions({ [field]: value }));
  };

  // Generate note
  const handleGenerateNote = () => {
    if (!transcript) {
      alert('No transcript available');
      return;
    }

    // Check if this is a regeneration
    const isRegeneration = !!generatedNote;

    // Resolve template content locally (sandbox transparency principle)
    const userTemplateContent = customNoteTemplateContent || selectedTemplate?.content || '';

    const config = {
      transcript,
      providerId: selectedProviderId || selectedCase?.provider_info_fk?.id,
      caseId: selectedCase?.id,
      userTemplateContent: userTemplateContent,
      llmPromptTemplateId: selectedLLMPromptTemplateId,
      customInstructions: customInstructions,
      customLLMPrompt,
      providerPreferencesOverride,
      priorNotesData: priorNotesConfig.enabled ? priorNotesConfig.sections : undefined,
      speakerLabels: selectedCase?.utterances || undefined,
      generationOptions,
      saveRun: true, // Auto-save all runs for debugging/replay
      runName: selectedCase ? `${selectedCase.CoreServer_CaseID} - ${new Date().toLocaleString()}` : undefined,
      runTags: selectedCase ? [selectedCase.CoreServer_CaseID] : []
    };

    dispatch(generateNote(config)).then((result) => {
      if (result.payload?.note_sections) {
        // Auto-convert to all formats using new API signature
        dispatch(convertNoteFormats({
          noteSections: result.payload.note_sections,
          contentType: 'all'
        }));

        // Show success message if this was a regeneration
        if (isRegeneration) {
          setMessage({ type: 'success', text: 'Note regenerated successfully!' });
          setTimeout(() => setMessage(null), 5000);
        }
      }
    });
  };

  // Convert note to specific format
  const handleConvertFormat = (format) => {
    if (!generatedNote) {
      alert('Generate a note first');
      return;
    }

    dispatch(convertNoteFormats({
      noteSections: generatedNote,
      contentType: format
    }));
  };

  // Filter cases based on search
  const filteredCases = availableCases.filter(caseItem => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      caseItem.CoreServer_CaseID?.toLowerCase().includes(searchLower) ||
      caseItem.patient_info_fk?.first_name?.toLowerCase().includes(searchLower) ||
      caseItem.patient_info_fk?.last_name?.toLowerCase().includes(searchLower) ||
      caseItem.mira_sol_case_status?.toLowerCase().includes(searchLower);

    const matchesHealthSystem = !selectedHealthSystem ||
      caseItem.healthcare_info_fk?.name === selectedHealthSystem;

    return matchesSearch && matchesHealthSystem;
  });

  // Get unique health systems from cases
  const healthSystems = [...new Set(availableCases.map(c => c.healthcare_info_fk?.name).filter(Boolean))];

  // Group providers by health system
  const groupedProviders = React.useMemo(() => {
    const groups = {};
    availableProviders.forEach(provider => {
      const healthSystemName = provider.health_system?.name || 'No Health System';
      if (!groups[healthSystemName]) {
        groups[healthSystemName] = [];
      }
      groups[healthSystemName].push(provider);
    });
    return groups;
  }, [availableProviders]);

  // Filter providers by search term
  const filteredProviders = React.useMemo(() => {
    if (!providerSearchTerm) return groupedProviders;

    const filtered = {};
    Object.keys(groupedProviders).forEach(healthSystem => {
      const matchingProviders = groupedProviders[healthSystem].filter(provider => {
        const searchLower = providerSearchTerm.toLowerCase();
        const fullName = `${provider.first_name} ${provider.last_name}`.toLowerCase();
        const specialty = provider.specialties?.[0]?.name?.toLowerCase() || '';
        return fullName.includes(searchLower) || specialty.includes(searchLower);
      });
      if (matchingProviders.length > 0) {
        filtered[healthSystem] = matchingProviders;
      }
    });
    return filtered;
  }, [groupedProviders, providerSearchTerm]);

  // Group templates by association level
  const groupedTemplates = React.useMemo(() => {
    const global = [];
    const healthSystemTemplates = {};
    const providerTemplates = [];

    availableTemplates.forEach(template => {
      // Check if template has any associations
      const hasHealthSystems = template.health_systems?.length > 0;
      const hasProviders = template.providers?.length > 0;
      const hasSpecialties = template.specialties?.length > 0;
      const hasDepartments = template.departments?.length > 0;
      const hasClinics = template.clinics?.length > 0;

      if (hasProviders) {
        providerTemplates.push(template);
      } else if (hasHealthSystems) {
        template.health_systems.forEach(hsId => {
          // Find health system name from available providers
          const hsName = availableProviders.find(p => p.health_system?.id === hsId)?.health_system?.name || `Health System ${hsId}`;
          if (!healthSystemTemplates[hsName]) {
            healthSystemTemplates[hsName] = [];
          }
          healthSystemTemplates[hsName].push(template);
        });
      } else if (!hasSpecialties && !hasDepartments && !hasClinics) {
        global.push(template);
      }
    });

    return { global, healthSystemTemplates, providerTemplates };
  }, [availableTemplates, availableProviders]);

  // Filter templates by search term
  const filteredTemplates = React.useMemo(() => {
    if (!templateSearchTerm) return groupedTemplates;

    const searchLower = templateSearchTerm.toLowerCase();
    const filterTemplates = (templates) =>
      templates.filter(t =>
        t.name?.toLowerCase().includes(searchLower) ||
        t.template_type?.toLowerCase().includes(searchLower) ||
        t.description?.toLowerCase().includes(searchLower)
      );

    return {
      global: filterTemplates(groupedTemplates.global),
      healthSystemTemplates: Object.keys(groupedTemplates.healthSystemTemplates).reduce((acc, hs) => {
        const filtered = filterTemplates(groupedTemplates.healthSystemTemplates[hs]);
        if (filtered.length > 0) acc[hs] = filtered;
        return acc;
      }, {}),
      providerTemplates: filterTemplates(groupedTemplates.providerTemplates)
    };
  }, [groupedTemplates, templateSearchTerm]);

  // Get selected provider/template display names
  const selectedProviderName = React.useMemo(() => {
    if (!selectedProviderId) return 'Use Case\'s Provider';
    const provider = availableProviders.find(p => (p.id || p.provider_id) === selectedProviderId);
    if (!provider) return 'Use Case\'s Provider';
    return `${provider.first_name} ${provider.last_name} - ${provider.specialties?.[0]?.name || 'No specialty'}`;
  }, [selectedProviderId, availableProviders]);

  const selectedTemplate = React.useMemo(() => {
    if (!selectedTemplateId) return null;
    return availableTemplates.find(t => (t.id || t.template_id) === selectedTemplateId) ?? null;
  }, [selectedTemplateId, availableTemplates]);

  const selectedTemplateName = React.useMemo(() => {
    if (!selectedTemplate) return 'Use Provider\'s Default Template';
    return `${selectedTemplate.name} (${selectedTemplate.template_type})`;
  }, [selectedTemplate]);

  // Load template content when template selection changes or editor opens
  // Only depends on selectedTemplateId to avoid resetting user edits when templates re-fetch
  useEffect(() => {
    if (showNoteTemplateEditor) {
      const template = availableTemplates.find(t => (t.id || t.template_id) === selectedTemplateId);
      dispatch(updateCustomNoteTemplateContent(template?.content || ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, showNoteTemplateEditor]);

  // Filter LLM prompt templates by search term
  const filteredLLMPromptTemplates = React.useMemo(() => {
    if (!llmPromptSearchTerm) return availableLLMPromptTemplates;

    const searchLower = llmPromptSearchTerm.toLowerCase();
    return availableLLMPromptTemplates.filter(prompt =>
      prompt.name?.toLowerCase().includes(searchLower) ||
      prompt.prompt_type?.toLowerCase().includes(searchLower) ||
      prompt.description?.toLowerCase().includes(searchLower)
    );
  }, [availableLLMPromptTemplates, llmPromptSearchTerm]);

  const selectedLLMPromptTemplateName = React.useMemo(() => {
    if (!selectedLLMPromptTemplateId) return 'Use System Default';
    const prompt = availableLLMPromptTemplates.find(p => p.id === selectedLLMPromptTemplateId);
    if (!prompt) return 'Use System Default';
    return `${prompt.name} (${prompt.prompt_type})`;
  }, [selectedLLMPromptTemplateId, availableLLMPromptTemplates]);

  // Shared CopyButton component
  const CopyButton = ({ text, fieldId, className = "btn btn-sm btn-outline-secondary" }) => (
    <button
      className={className}
      onClick={() => copyToClipboard(text, fieldId)}
      title="Copy to clipboard"
      disabled={!text}
    >
      <i className={`fas ${copiedField === fieldId ? 'fa-check text-success' : 'fa-copy'} me-1`}></i>
      {copiedField === fieldId ? 'Copied!' : ''}
    </button>
  );

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Notes Sandbox</h4>
            <div className="page-title-right">
              <button
                className="btn btn-outline-info btn-sm me-2"
                onClick={() => setShowArchitecture(!showArchitecture)}
              >
                <i className={`fas fa-${showArchitecture ? 'eye-slash' : 'project-diagram'} me-1`}></i>
                {showArchitecture ? 'Hide' : 'Show'} Architecture
              </button>
              <button
                className="btn btn-outline-primary btn-sm me-2"
                onClick={() => setShowPipelineRunsPanel(true)}
              >
                <i className="fas fa-history me-1"></i>
                Pipeline Runs
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => dispatch(resetSandbox())}
              >
                <i className="fas fa-redo me-1"></i>
                Reset All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Message Display */}
      {message && (
        <div className="row mb-3">
          <div className="col-12">
            <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show py-2`} role="alert">
              <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label="Close"></button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message Display */}
      {generateError && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show py-2" role="alert">
              <i className="fas fa-exclamation-circle me-2"></i>
              <strong>{generateError.error || 'Note Generation Failed'}</strong>
              {generateError.detail && (
                <span className="ms-2">
                  {(() => {
                    const detail = generateError.detail;
                    if (typeof detail === 'string') {
                      const messageMatch = detail.match(/'message':\s*"([^"]+)"/);
                      return messageMatch ? messageMatch[1] : detail;
                    }
                    return JSON.stringify(detail);
                  })()}
                </span>
              )}
              <button type="button" className="btn-close" onClick={() => dispatch(generateNote.rejected(null))} aria-label="Close"></button>
            </div>
          </div>
        </div>
      )}

      {/* Architecture Diagram */}
      {showArchitecture && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-info">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">
                  <i className="fas fa-project-diagram me-2"></i>
                  Note Generation Architecture
                </h5>
              </div>
              <div className="card-body bg-light">
                <div className="row">
                  {/* Step 1: Template Selection */}
                  <div className="col-md-3 mb-3">
                    <div className="card h-100 border-primary">
                      <div className="card-header bg-primary text-white">
                        <strong>Step 1: Template Selection</strong>
                      </div>
                      <div className="card-body p-2">
                        <small>
                          <div className="mb-2">
                            <strong className="text-primary">Two Templates Required:</strong>
                          </div>
                          <div className="mb-2">
                            <strong>1. System LLM Prompt</strong>
                            <ul className="ps-3 mb-1" style={{ fontSize: '0.8rem' }}>
                              <li>Master instructions</li>
                              <li>Filtered by health system</li>
                              <li>Cached for 30 days in PROD, none in sandbox</li>
                            </ul>
                          </div>
                          <div className="mb-2">
                            <strong>2. User Note Template</strong>
                            <ul className="ps-3 mb-0" style={{ fontSize: '0.8rem' }}>
                              <li>Sandbox override OR</li>
                              <li>Provider default OR</li>
                              <li>Provider favorites OR</li>
                              <li>Health system templates OR</li>
                              <li>Specialty/Dept/Clinic OR</li>
                              <li>Active template (fallback)</li>
                            </ul>
                          </div>
                          <div className="mt-2 p-2 bg-light rounded">
                            <strong className="text-success">Output:</strong> System LLM Prompt + User Note Template
                          </div>
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Prompt Building */}
                  <div className="col-md-3 mb-3">
                    <div className="card h-100 border-warning">
                      <div className="card-header bg-warning text-dark">
                        <strong>Step 2: Prompt Construction</strong>
                      </div>
                      <div className="card-body p-2">
                        <small>
                          <div className="mb-2">
                            <strong className="text-warning">System Message:</strong>
                          </div>
                          <ul className="ps-3 mb-2" style={{ fontSize: '0.8rem' }}>
                            <li>LLM Prompt Template text</li>
                            <li>Provider preferences (brevity, style)</li>
                            <li>Custom instructions</li>
                          </ul>
                          <div className="mb-2">
                            <strong className="text-warning">User Prompt:</strong>
                          </div>
                          <ul className="ps-3 mb-0" style={{ fontSize: '0.8rem' }}>
                            <li>Transcript (full text)</li>
                            <li>Speaker labels/diarization</li>
                            <li>Prior notes (if enabled)</li>
                            <li>User note template (if not available, defaults to healthcare/specialty/department/clinic/global hierarchy)</li>
                          </ul>
                          <div className="mt-2 p-2 bg-light rounded">
                            <strong className="text-success">Output:</strong> Final system + user prompts
                          </div>
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Step 3: LLM Generation */}
                  <div className="col-md-3 mb-3">
                    <div className="card h-100 border-success">
                      <div className="card-header bg-success text-white">
                        <strong>Step 3: Note Generation</strong>
                      </div>
                      <div className="card-body p-2">
                        <small>
                          <div className="mb-2">
                            <strong className="text-success">API Configuration:</strong>
                          </div>
                          <ul className="ps-3 mb-2" style={{ fontSize: '0.8rem' }}>
                            <li><strong>Model:</strong> Configurable (hardcoded)</li>
                            <li><strong>Temperature:</strong> 0.0-0.2 (configurable)</li>
                            <li><strong>Max Tokens:</strong> 15000 (configurable)</li>
                            <li><strong>Reasoning:</strong> None (allows temp/top_p)</li>
                            <li><strong>Format:</strong> Structured JSON</li>
                          </ul>
                          <div className="mb-2">
                            <strong className="text-success">Sections Generated:</strong>
                          </div>
                          <ul className="ps-3 mb-0" style={{ fontSize: '0.75rem' }}>
                            <li>Defined by Note Template</li>
                            <li>E.g., CC, HPI, PE, Assessment, Plan</li>
                          </ul>
                          <div className="mt-2 p-2 bg-light rounded">
                            <strong className="text-success">Output:</strong> note_sections (JSON)
                          </div>
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Format Conversion */}
                  <div className="col-md-3 mb-3">
                    <div className="card h-100 border-info">
                      <div className="card-header bg-info text-white">
                        <strong>Step 4: Format Conversion</strong>
                      </div>
                      <div className="card-body p-2">
                        <small>
                          <div className="mb-2">
                            <strong className="text-info">Backend Rendering:</strong>
                          </div>
                          <ul className="ps-3 mb-2" style={{ fontSize: '0.8rem' }}>
                            <li><strong>HTML:</strong> Styled document with headers, lists, tables</li>
                            <li><strong>Plain Text:</strong> Clean readable format without markup</li>
                            <li><strong>RTF:</strong> Rich Text Format for EMR/EHR systems</li>
                          </ul>
                          <div className="mb-2">
                            <strong className="text-info">Rendering Features:</strong>
                          </div>
                          <ul className="ps-3 mb-0" style={{ fontSize: '0.75rem' }}>
                            <li>Section headers</li>
                            <li>Bullet/numbered lists</li>
                            <li>Tables (vitals, meds)</li>
                            <li>Proper formatting</li>
                          </ul>
                          <div className="mt-2 p-2 bg-light rounded">
                            <strong className="text-success">Output:</strong> HTML / Plain Text / RTF
                          </div>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="row mt-2">
                  <div className="col-12">
                    <div className="alert alert-info mb-0 py-2">
                      <small>
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>This sandbox lets you override any component:</strong> Mix and match cases, transcripts, providers, and templates to test different combinations and identify bugs in the note generation pipeline.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Layout - Three Column */}
      <div className="row">
        {/* Left Column - Case Browser */}
        <div className="col-lg-3">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fas fa-folder-open me-2"></i>
                Cases
              </h5>
            </div>
            <div className="card-body">
              {/* Health System Filter */}
              <div className="mb-3">
                <select
                  className="form-select form-select-sm"
                  value={selectedHealthSystem}
                  onChange={(e) => setSelectedHealthSystem(e.target.value)}
                >
                  <option value="">All Health Systems</option>
                  {healthSystems.map(hs => (
                    <option key={hs} value={hs}>{hs}</option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search cases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Case List */}
              <div className="list-group" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {filteredCases.slice(0, 50).map(caseItem => (
                  <button
                    key={caseItem.CoreServer_CaseID}
                    type="button"
                    className={`list-group-item list-group-item-action ${
                      selectedCase?.CoreServer_CaseID === caseItem.CoreServer_CaseID ? 'active' : ''
                    }`}
                    onClick={() => handleSelectCase(caseItem)}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <small className="mb-1">
                        <strong>
                          {caseItem.patient_info_fk?.first_name} {caseItem.patient_info_fk?.last_name}
                        </strong>
                      </small>
                      <small>
                        <span className={`badge bg-${
                          caseItem.mira_sol_case_status === 'completed' ? 'success' :
                          caseItem.mira_sol_case_status === 'in_progress' ? 'primary' :
                          'secondary'
                        }`}>
                          {caseItem.mira_sol_case_status}
                        </span>
                      </small>
                    </div>
                    <small className="text-muted d-block">
                      {caseItem.CoreServer_CaseID?.substring(0, 8)}...
                    </small>
                  </button>
                ))}
                {filteredCases.length === 0 && (
                  <div className="text-center text-muted p-3">
                    <i className="fas fa-search mb-2"></i>
                    <p className="mb-0">No cases found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column - Configuration */}
        <div className="col-lg-5">
          {/* Component Overrides */}
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fas fa-sliders-h me-2"></i>
                Pipeline Components
              </h5>
            </div>
            <div className="card-body">
              {!selectedCase ? (
                <div className="text-center text-muted p-4">
                  <i className="fas fa-arrow-left mb-2" style={{ fontSize: '2rem' }}></i>
                  <p className="mb-0">Select a case to begin</p>
                </div>
              ) : (
                <>
                  {/* Case Info */}
                  <div className="alert alert-info mb-3">
                    <div className="mb-2">
                      <strong>{selectedCase.patient_info_fk?.first_name} {selectedCase.patient_info_fk?.last_name}</strong>
                      <span className="ms-2">({new Date(selectedCase.patient_info_fk?.dob).toLocaleDateString()})</span>
                    </div>
                    <div className="row small">
                      <div className="col-sm-6">
                        <strong>Core Server ID:</strong> {selectedCase.CoreServer_CaseID}
                      </div>
                      <div className="col-sm-6" style={{ wordBreak: 'break-all' }}>
                        <strong>MiraSol ID:</strong> {selectedCase.id}
                      </div>
                      <div className="col-sm-6">
                        <strong>Provider:</strong> {selectedCase.provider_info_fk?.first_name} {selectedCase.provider_info_fk?.last_name}
                      </div>
                      <div className="col-sm-6">
                        <strong>Healthcare:</strong> {selectedCase.healthcare_info_fk?.name}
                      </div>
                      <div className="col-sm-6">
                        <strong>Created:</strong> {new Date(selectedCase.created_at).toLocaleString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: '2-digit',
                          hour: 'numeric',
                          minute: '2-digit',
                          timeZoneName: 'short'
                        })}
                      </div>
                      <div className="col-sm-6">
                        <strong>Updated:</strong> {new Date(selectedCase.updated_at).toLocaleString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: '2-digit',
                          hour: 'numeric',
                          minute: '2-digit',
                          timeZoneName: 'short'
                        })}
                    </div>
                    </div>
                  </div>

                  {/* LLM Prompt Template Override */}
                  <div className="mb-3" ref={llmPromptDropdownRef}>
                    <label className="form-label">System LLM Prompt <span className="text-danger">*</span></label>

                    {/* Selected LLM Prompt Template Display */}
                    <div className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={selectedLLMPromptTemplateName}
                        readOnly
                        style={{ backgroundColor: 'white' }}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowLLMPromptDropdown(!showLLMPromptDropdown)}
                      >
                        <i className={`fas fa-chevron-${showLLMPromptDropdown ? 'up' : 'down'}`}></i>
                      </button>
                      {selectedLLMPromptTemplateId && (
                        <button
                          className="btn btn-outline-danger"
                          type="button"
                          onClick={() => dispatch(setSelectedLLMPromptTemplate(null))}
                          title="Clear selection"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>

                    {/* Searchable Dropdown */}
                    {showLLMPromptDropdown && (
                      <div className="border rounded p-2 bg-light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          placeholder="Search LLM prompts..."
                          value={llmPromptSearchTerm}
                          onChange={(e) => setLLMPromptSearchTerm(e.target.value)}
                          autoFocus
                        />

                        {llmPromptTemplatesStatus === 'loading' ? (
                          <div className="text-center p-2">
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Loading templates...
                          </div>
                        ) : filteredLLMPromptTemplates.length === 0 ? (
                          <div className="alert alert-warning text-center p-3 small">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            No LLM prompt templates found. Please create system-level prompts in <a href="/admin/note-templates" className="alert-link">Master LLM Prompts</a> to enable note generation.
                          </div>
                        ) : (
                          filteredLLMPromptTemplates.map(prompt => (
                            <div
                              key={prompt.id}
                              className={`p-2 mb-1 rounded cursor-pointer ${
                                prompt.id === selectedLLMPromptTemplateId ? 'bg-primary text-white' : 'bg-white'
                              }`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                dispatch(setSelectedLLMPromptTemplate(prompt.id));
                                setShowLLMPromptDropdown(false);
                                setLLMPromptSearchTerm('');
                              }}
                            >
                              <div className="small">
                                <strong>{prompt.name}</strong>
                                <span className="ms-2 badge bg-info">{prompt.prompt_type}</span>
                                {prompt.version && <span className="ms-1 badge bg-secondary">v{prompt.version}</span>}
                              </div>
                              {prompt.description && (
                                <div className="small text-muted">{prompt.description}</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* LLM Prompt Editor (shown when template is selected) */}
                  {selectedLLMPromptTemplateId && (
                    <div className="mb-3">
                      <button
                        className="btn btn-outline-info btn-sm w-100"
                        onClick={() => setShowLLMPromptEditor(!showLLMPromptEditor)}
                      >
                        <i className={`fas fa-${showLLMPromptEditor ? 'chevron-up' : 'code'} me-2`}></i>
                        Edit LLM Prompt Template
                        {customLLMPrompt.isCustomized && <span className="badge bg-warning ms-2">Modified</span>}
                      </button>
                    </div>
                  )}

                  {showLLMPromptEditor && selectedLLMPromptTemplateId && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      {/* System Message */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="row" style={{maxWidth:'70%'}}>
                            <label className="form-label small fw-bold mb-0">System Message</label>
                            <small className="text-muted">Master instructions for the LLM. In PROD, these are cached for 30 days unless manually cleared. Also, by default, these are filtered by the health system for PROVIDER generating the note.</small>
                          </div>
                          <div>
                            {customLLMPrompt.isCustomized && (<button className="btn btn-outline-secondary btn-sm me-2" onClick={() => dispatch(resetCustomLLMPrompt())} >
                            <i className="fas fa-undo me-1"></i>Reset
                            </button> )}                          
                            <CopyButton text={customLLMPrompt.system_message} fieldId="llm-system-msg" />
                          </div>
                        </div>
                        <textarea
                          className="form-control form-control-sm font-monospace"
                          rows="6"
                          placeholder="System message for the LLM..."
                          value={customLLMPrompt.system_message}
                          onChange={(e) => dispatch(updateCustomLLMPrompt({ system_message: e.target.value }))}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Provider Override */}
                  <div className="mb-3" ref={providerDropdownRef}>
                    <label className="form-label">Provider <span className="text-danger">*</span></label>

                    {/* Selected Provider Display */}
                    <div className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={selectedProviderName}
                        readOnly
                        style={{ backgroundColor: 'white' }}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowProviderDropdown(!showProviderDropdown)}
                      >
                        <i className={`fas fa-chevron-${showProviderDropdown ? 'up' : 'down'}`}></i>
                      </button>
                      {selectedProviderId && (
                        <button
                          className="btn btn-outline-danger"
                          type="button"
                          onClick={() => dispatch(setSelectedProvider(null))}
                          title="Clear selection"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>

                    {/* Searchable Dropdown */}
                    {showProviderDropdown && (
                      <div className="border rounded p-2 bg-light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          placeholder="Search providers..."
                          value={providerSearchTerm}
                          onChange={(e) => setProviderSearchTerm(e.target.value)}
                          autoFocus
                        />

                        {Object.keys(filteredProviders).length === 0 ? (
                          <div className="text-muted text-center p-2">No providers found</div>
                        ) : (
                          Object.keys(filteredProviders).sort().map(healthSystem => (
                            <div key={healthSystem} className="mb-2">
                              <div className="fw-bold text-primary small mb-1">
                                <i className="fas fa-hospital me-1"></i>
                                {healthSystem}
                              </div>
                              {filteredProviders[healthSystem].map(provider => (
                                <div
                                  key={provider.id || provider.provider_id}
                                  className={`p-2 mb-1 rounded cursor-pointer ${
                                    (provider.id || provider.provider_id) === selectedProviderId ? 'bg-primary text-white' : 'bg-white'
                                  }`}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    dispatch(setSelectedProvider(provider.id || provider.provider_id));
                                    setShowProviderDropdown(false);
                                    setProviderSearchTerm('');
                                  }}
                                >
                                  <div className="small">
                                    <strong>{provider.first_name} {provider.last_name}</strong>
                                  </div>
                                  <div className="small text-muted">
                                    {provider.specialties?.[0]?.name || 'No specialty'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Template Override */}
                  <div className="mb-3" ref={templateDropdownRef}>
                    <label className="form-label">Note Template</label>

                    {/* Selected Template Display */}
                    <div className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={selectedTemplateName}
                        readOnly
                        style={{ backgroundColor: 'white' }}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                      >
                        <i className={`fas fa-chevron-${showTemplateDropdown ? 'up' : 'down'}`}></i>
                      </button>
                      {selectedTemplateId && (
                        <button
                          className="btn btn-outline-danger"
                          type="button"
                          onClick={() => dispatch(setSelectedTemplate(null))}
                          title="Clear selection"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>

                    {/* Searchable Dropdown */}
                    {showTemplateDropdown && (
                      <div className="border rounded p-2 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          placeholder="Search templates..."
                          value={templateSearchTerm}
                          onChange={(e) => setTemplateSearchTerm(e.target.value)}
                          autoFocus
                        />

                        {/* Global Templates */}
                        {filteredTemplates.global.length > 0 && (
                          <div className="mb-3">
                            <div className="fw-bold text-success small mb-1">
                              <i className="fas fa-globe me-1"></i>
                              Global Templates
                            </div>
                            {filteredTemplates.global.map(template => (
                              <div
                                key={template.id || template.template_id}
                                className={`p-2 mb-1 rounded cursor-pointer ${
                                  (template.id || template.template_id) === selectedTemplateId ? 'bg-primary text-white' : 'bg-white'
                                }`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  dispatch(setSelectedTemplate(template.id || template.template_id));
                                  setShowTemplateDropdown(false);
                                  setTemplateSearchTerm('');
                                }}
                              >
                                <div className="small">
                                  <strong>{template.name}</strong>
                                  <span className="ms-2 badge bg-secondary">{template.template_type}</span>
                                </div>
                                {template.description && (
                                  <div className="small text-muted">{template.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Health System Templates */}
                        {Object.keys(filteredTemplates.healthSystemTemplates).length > 0 && (
                          <div className="mb-3">
                            {Object.keys(filteredTemplates.healthSystemTemplates).sort().map(healthSystem => (
                              <div key={healthSystem} className="mb-2">
                                <div className="fw-bold text-primary small mb-1">
                                  <i className="fas fa-hospital me-1"></i>
                                  {healthSystem}
                                </div>
                                {filteredTemplates.healthSystemTemplates[healthSystem].map(template => (
                                  <div
                                    key={template.id || template.template_id}
                                    className={`p-2 mb-1 rounded cursor-pointer ${
                                      (template.id || template.template_id) === selectedTemplateId ? 'bg-primary text-white' : 'bg-white'
                                    }`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      dispatch(setSelectedTemplate(template.id || template.template_id));
                                      setShowTemplateDropdown(false);
                                      setTemplateSearchTerm('');
                                    }}
                                  >
                                    <div className="small">
                                      <strong>{template.name}</strong>
                                      <span className="ms-2 badge bg-secondary">{template.template_type}</span>
                                    </div>
                                    {template.description && (
                                      <div className="small text-muted">{template.description}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Provider-Specific Templates */}
                        {filteredTemplates.providerTemplates.length > 0 && (
                          <div className="mb-3">
                            <div className="fw-bold text-warning small mb-1">
                              <i className="fas fa-user-md me-1"></i>
                              Provider-Specific Templates
                            </div>
                            {filteredTemplates.providerTemplates.map(template => (
                              <div
                                key={template.id || template.template_id}
                                className={`p-2 mb-1 rounded cursor-pointer ${
                                  (template.id || template.template_id) === selectedTemplateId ? 'bg-primary text-white' : 'bg-white'
                                }`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  dispatch(setSelectedTemplate(template.id || template.template_id));
                                  setShowTemplateDropdown(false);
                                  setTemplateSearchTerm('');
                                }}
                              >
                                <div className="small">
                                  <strong>{template.name}</strong>
                                  <span className="ms-2 badge bg-secondary">{template.template_type}</span>
                                </div>
                                {template.description && (
                                  <div className="small text-muted">{template.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* No Results */}
                        {filteredTemplates.global.length === 0 &&
                         Object.keys(filteredTemplates.healthSystemTemplates).length === 0 &&
                         filteredTemplates.providerTemplates.length === 0 && (
                          <div className="text-muted text-center p-2">No templates found</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Edit Note Template - shown when a template is selected */}
                  {selectedTemplateId && selectedTemplate && (
                    <div className="mb-3">
                      <button
                        className="btn btn-outline-info btn-sm w-100"
                        onClick={() => {
                          // Initialize with template content when first opening editor
                          if (!showNoteTemplateEditor && !customNoteTemplateContent) {
                            dispatch(updateCustomNoteTemplateContent(selectedTemplate.content || ''));
                          }
                          setShowNoteTemplateEditor(!showNoteTemplateEditor);
                        }}
                      >
                        <i className={`fas fa-${showNoteTemplateEditor ? 'chevron-up' : 'edit'} me-2`}></i>
                        Edit Note Template
                        {customNoteTemplateContent && customNoteTemplateContent !== selectedTemplate?.content && (
                          <span className="badge bg-warning ms-2">Modified</span>
                        )}
                      </button>
                    </div>
                  )}

                  {showNoteTemplateEditor && selectedTemplateId && selectedTemplate && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label small fw-bold mb-0">Note Template Content</label>
                        <div>
                        {customNoteTemplateContent && customNoteTemplateContent !== selectedTemplate?.content && (
                        <button
                          className="btn btn-outline-secondary btn-sm me-2"
                          onClick={() => dispatch(updateCustomNoteTemplateContent(selectedTemplate?.content || ''))}
                        ><i className="fas fa-undo me-1"></i>
                          Reset
                        </button>
                      )}
                        <CopyButton
                          text={customNoteTemplateContent}
                          fieldId="note-template-content"
                        />
                        </div>
                      </div>
                      <small className="text-muted d-block mb-2">
                        Structure sent to the LLM as &quot;Template Structure&quot;. Edits here override the saved template for this run only.
                      </small>
                      <textarea
                        className="form-control form-control-sm font-monospace"
                        rows={10}
                        placeholder="Template structure (sections, instructions)..."
                        value={customNoteTemplateContent}
                        onChange={(e) => dispatch(updateCustomNoteTemplateContent(e.target.value))}
                        style={{ fontSize: '0.85rem' }}
                      />
                    </div>
                  )}

                  {/* Provider Preferences Override */}
                  <div className="mb-3">
                    <button
                      className="btn btn-outline-secondary btn-sm w-100"
                      onClick={() => setShowPreferences(!showPreferences)}
                    >
                      <i className={`fas fa-${showPreferences ? 'chevron-up' : 'sliders-h'} me-2`}></i>
                      Provider Preferences
                    </button>
                  </div>

                  {showPreferences && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label small">Note Brevity</label>
                          <select
                            className="form-select form-select-sm"
                            value={providerPreferencesOverride.note_brevity || ''}
                            onChange={(e) => dispatch(updateProviderPreferences({ note_brevity: e.target.value || null }))}
                          >
                            <option value="">Use Provider Default</option>
                            <option value="brief">Brief</option>
                            <option value="standard">Standard</option>
                            <option value="detailed">Detailed</option>
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label small">Writing Style</label>
                          <select
                            className="form-select form-select-sm"
                            value={providerPreferencesOverride.writing_style || ''}
                            onChange={(e) => dispatch(updateProviderPreferences({ writing_style: e.target.value || null }))}
                          >
                            <option value="">Use Provider Default</option>
                            <option value="professional">Professional</option>
                            <option value="conversational">Conversational</option>
                            <option value="clinical">Clinical</option>
                          </select>
                        </div>

                        <div className="col-12">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="includeDifferential"
                              checked={providerPreferencesOverride.include_differential === true}
                              onChange={(e) => dispatch(updateProviderPreferences({ include_differential: e.target.checked ? true : null }))}
                            />
                            <label className="form-check-label small" htmlFor="includeDifferential">
                              Include Differential Diagnosis
                            </label>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="emphasizePlan"
                              checked={providerPreferencesOverride.emphasize_plan === true}
                              onChange={(e) => dispatch(updateProviderPreferences({ emphasize_plan: e.target.checked ? true : null }))}
                            />
                            <label className="form-check-label small" htmlFor="emphasizePlan">
                              Emphasize Treatment Plan
                            </label>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="patientEducation"
                              checked={providerPreferencesOverride.patient_education_focus === true}
                              onChange={(e) => dispatch(updateProviderPreferences({ patient_education_focus: e.target.checked ? true : null }))}
                            />
                            <label className="form-check-label small" htmlFor="patientEducation">
                              Focus on Patient Education
                            </label>
                          </div>
                        </div>

                        <div className="col-12 mt-2">
                          <button
                            className="btn btn-link btn-sm p-0"
                            onClick={() => dispatch(resetProviderPreferences())}
                          >
                            Reset to Defaults
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Prior Notes Configuration */}
                  <div className="mb-3">
                    <button
                      className="btn btn-outline-secondary btn-sm w-100"
                      onClick={() => setShowPriorNotes(!showPriorNotes)}
                    >
                      <i className={`fas fa-${showPriorNotes ? 'chevron-up' : 'history'} me-2`}></i>
                      Prior Notes Integration
                      {priorNotesConfig.enabled && <span className="badge bg-success ms-2">{priorNotesConfig.sections.length}</span>}
                    </button>
                  </div>

                  {showPriorNotes && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="enablePriorNotes"
                          checked={priorNotesConfig.enabled}
                          onChange={(e) => dispatch(updatePriorNotesConfig({ enabled: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="enablePriorNotes">
                          Enable Prior Notes Integration
                        </label>
                      </div>

                      {priorNotesConfig.enabled && (
                        <>
                          <div className="mb-3">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                const newSection = {
                                  section_name: 'New Section',
                                  content: '',
                                  handling: 'context'
                                };
                                dispatch(addPriorNoteSection(newSection));
                              }}
                            >
                              <i className="fas fa-plus me-1"></i>
                              Add Prior Note Section
                            </button>
                          </div>

                          {priorNotesConfig.sections.map((section, index) => (
                            <div key={index} className="card mb-2">
                              <div className="card-body p-2">
                                <div className="row">
                                  <div className="col-md-5 mb-2">
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Section name"
                                      value={section.section_name}
                                      onChange={(e) => {
                                        const updatedSections = [...priorNotesConfig.sections];
                                        updatedSections[index] = { ...section, section_name: e.target.value };
                                        dispatch(updatePriorNotesConfig({ sections: updatedSections }));
                                      }}
                                    />
                                  </div>
                                  <div className="col-md-5 mb-2">
                                    <select
                                      className="form-select form-select-sm"
                                      value={section.handling}
                                      onChange={(e) => {
                                        const updatedSections = [...priorNotesConfig.sections];
                                        updatedSections[index] = { ...section, handling: e.target.value };
                                        dispatch(updatePriorNotesConfig({ sections: updatedSections }));
                                      }}
                                    >
                                      <option value="context">Use as Context</option>
                                      <option value="as-is">Include As-Is</option>
                                    </select>
                                  </div>
                                  <div className="col-md-2 mb-2">
                                    <button
                                      className="btn btn-sm btn-danger w-100"
                                      onClick={() => dispatch(removePriorNoteSection(index))}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                  <div className="col-12">
                                    <textarea
                                      className="form-control form-control-sm"
                                      rows="3"
                                      placeholder="Prior note content..."
                                      value={section.content}
                                      onChange={(e) => {
                                        const updatedSections = [...priorNotesConfig.sections];
                                        updatedSections[index] = { ...section, content: e.target.value };
                                        dispatch(updatePriorNotesConfig({ sections: updatedSections }));
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {priorNotesConfig.sections.length > 0 && (
                            <button
                              className="btn btn-link btn-sm p-0 mt-2"
                              onClick={() => dispatch(clearPriorNotes())}
                            >
                              Clear All Prior Notes
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0">Transcript <span className="text-danger">*</span></label>
                      <div>{transcriptEdited && (
                        <button
                          className="btn btn-outline-secondary btn-sm me-2"
                          onClick={handleResetTranscript}
                        ><i className="fas fa-undo me-1"></i>
                          Reset
                        </button>
                      )}
                      <CopyButton text={transcript} fieldId="transcript" /></div>
                    </div>
                    <textarea
                      className="form-control font-monospace"
                      rows={10}
                      value={transcript}
                      onChange={handleTranscriptChange}
                      placeholder="No transcript available"
                    />
                    <small className="text-muted">
                      {transcript.length} characters
                    </small>
                  </div>
                  
                  {/* Custom Instructions */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0">Custom Instructions (Optional)</label>
                      <CopyButton text={customInstructions} fieldId="custom-instructions" />
                    </div>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={customInstructions}
                      onChange={handleCustomInstructionsChange}
                      placeholder="Add any custom instructions for note generation..."
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Generation Config */}
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fas fa-cog me-2"></i>
                Generation Configuration
              </h5>
            </div>
            <div className="card-body bg-light">

              {/* Advanced Options */}
              <div className="border rounded p-3 mb-3">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Model <span className="text-danger">*</span>
                      {modelsStatus === 'loading' && (
                        <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                      )}
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={generationOptions.model}
                      onChange={(e) => handleGenerationOptionChange('model', e.target.value)}
                      disabled={modelsStatus === 'loading'}
                    >
                      {modelsStatus === 'loading' ? (
                        <option>Loading models...</option>
                      ) : modelsStatus === 'error' ? (
                        <>
                          <option value="gpt-4o">GPT-4o (fallback)</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo (fallback)</option>
                        </>
                      ) : availableModels.length > 0 ? (
                        availableModels
                          .filter(model => model.id.startsWith('gpt'))
                          .reverse()
                          .map(model => (
                            <option key={model.id} value={model.id}>
                              {model.id}
                              {model.owned_by !== 'openai' && ` (${model.owned_by})`}
                            </option>
                          ))
                      ) : (
                        <>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          <option value="gpt-4">GPT-4</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        </>
                      )}
                    </select>
                    {modelsStatus === 'error' && (
                      <small className="text-danger">
                        Failed to load models. Using fallback options.
                      </small>
                    )}
                    {modelsStatus === 'success' && availableModels.length > 0 && (
                      <small className="text-muted">
                        {availableModels.filter(m => m.id.startsWith('gpt')).length} GPT models available
                      </small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Reasoning Effort</label>
                    <select
                      className="form-select form-select-sm"
                      value={generationOptions.reasoning_effort || 'none'}
                      disabled
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    >
                      <option value="none">None (allows temperature/top_p)</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="xhigh">X-High</option>
                    </select>
                    <small className="text-muted">
                      Fixed to &apos;none&apos; for GPT-5+ parameter compatibility
                    </small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Temperature</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min="0"
                      max="2"
                      step="0.1"
                      value={generationOptions.temperature}
                      onChange={(e) => handleGenerationOptionChange('temperature', parseFloat(e.target.value))}
                    />
                    <small className="text-muted">Temp = 0.0-0.2 for highest consistency. 0 does NOT mean identical outputs</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Max Tokens</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min="100"
                      max="10000"
                      step="100"
                      value={generationOptions.max_tokens}
                      onChange={(e) => handleGenerationOptionChange('max_tokens', parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <button
                className="btn btn-generate-note w-100 d-flex justify-content-center align-items-center"
                onClick={handleGenerateNote}
                disabled={!selectedCase || !transcript || generateStatus === 'loading'}
              >
                {generateStatus === 'loading' ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-wand-magic-sparkles me-2"></i>
                    Generate Note
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Final Prompt Display - Shows what was sent to LLM */}
          {(finalUserMessage || finalSystemMessage) && (
            <div className="card mb-3">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-code me-2"></i>
                  Final Prompt Sent to LLM
                </h5>
              </div>
              <div className="card-body">
                {/* System Message */}
                {finalSystemMessage && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold mb-0">
                        <i className="fas fa-robot me-1"></i>
                        System Message
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">
                          {finalSystemMessage.length} characters
                        </small>
                        <CopyButton text={finalSystemMessage} fieldId="final-system-msg" />
                      </div>
                    </div>
                    <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem' }}>
                      {finalSystemMessage}
                    </pre>
                  </div>
                )}

                {/* User Message */}
                {finalUserMessage && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold mb-0">
                        <i className="fas fa-comment-dots me-1"></i>
                        User Prompt
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">
                          {finalUserMessage.length} characters
                        </small>
                        <CopyButton text={finalUserMessage} fieldId="final-user-msg" />
                      </div>
                    </div>
                    <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.85rem' }}>
                      {finalUserMessage}
                    </pre>
                    <small className="text-muted"><strong>Sandbox vs PROD: </strong>If you send empty user_note_template in sandbox, it will not be used for note generation. However, in PROD, system will always try to find user&apos;s default note template (with fallbacks to healthcare/specialty/department/clinic/global).</small>
                  </div>
                )}

                <div className="alert alert-info mt-3 mb-0 py-2">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    This is the exact prompt sent to the LLM for note generation. Use this to debug issues with generated notes.
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Results */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fas fa-file-medical-alt me-2"></i>
                Generated Note
              </h5>
            </div>
            <div className="card-body">
              {!generatedNote ? (
                <div className="text-center text-muted p-4">
                  <i className="fas fa-clipboard-list mb-2" style={{ fontSize: '2rem' }}></i>
                  <p className="mb-0">Generate a note to see results</p>
                </div>
              ) : (
                <>
                  {/* Format Tabs */}
                  <ul className="nav nav-tabs mb-3" role="tablist">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'json' ? 'active' : ''}`}
                        onClick={() => setActiveTabLocal('json')}
                      >
                        Raw JSON Output
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'html' ? 'active' : ''}`}
                        onClick={() => setActiveTabLocal('html')}
                      >
                        HTML
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'plain' ? 'active' : ''}`}
                        onClick={() => setActiveTabLocal('plain')}
                      >
                        Plain Text
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'rtf' ? 'active' : ''}`}
                        onClick={() => setActiveTabLocal('rtf')}
                      >
                        RTF
                      </button>
                    </li>
                  </ul>

                  {/* Tab Content */}
                  <div className="tab-content" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {activeTab === 'json' && (
                      <div className="tab-pane fade show active">
                        <div className="d-flex justify-content-end mb-2">
                          <CopyButton
                            text={JSON.stringify(generatedNote, null, 2)}
                            fieldId="generated-json"
                            className="btn btn-sm btn-outline-light"
                          />
                        </div>
                        <pre className="bg-dark text-light p-3 rounded">
                          {JSON.stringify(generatedNote, null, 2)}
                        </pre>
                      </div>
                    )}
                    {activeTab === 'html' && (
                      <div className="tab-pane fade show active">
                        {/* Generate HTML Button and Toggle */}
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleConvertFormat('html')}
                            disabled={!generatedNote || convertStatus === 'loading'}
                          >
                            {convertStatus === 'loading' ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Generating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-sync-alt me-1"></i>
                                {generatedFormats.html ? 'Regenerate HTML' : 'Generate HTML'}
                              </>
                            )}
                          </button>

                          {/* Toggle between raw and formatted HTML */}
                          {generatedFormats.html && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowRawHtml(!showRawHtml)}
                              >
                                <i className={`fas ${showRawHtml ? 'fa-eye' : 'fa-code'} me-1`}></i>
                                {showRawHtml ? 'Show Formatted' : 'Show Raw HTML'}
                              </button>
                              <CopyButton text={generatedFormats.html} fieldId="generated-html" />
                            </>
                          )}

                          {convertError && (
                            <small className="text-danger ms-2">
                              Error: {convertError.error || 'Failed to generate HTML'}
                            </small>
                          )}
                        </div>

                        {generatedFormats.html ? (
                          showRawHtml ? (
                            // Raw HTML source code view
                            <pre className="border rounded p-3 bg-dark text-light" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                              <code>{generatedFormats.html}</code>
                            </pre>
                          ) : (
                            // Formatted HTML rendered view - use iframe for proper isolated styling
                            <iframe
                              title="Rendered HTML Note"
                              className="border rounded"
                              style={{
                                width: '100%',
                                height: '500px',
                                backgroundColor: 'white'
                              }}
                              srcDoc={generatedFormats.html}
                            />
                          )
                        ) : (
                          <div className="text-center text-muted p-4 border rounded">
                            <i className="fas fa-code mb-2" style={{ fontSize: '2rem' }}></i>
                            <p className="mb-0">Click &quot;Generate HTML&quot; to render this note</p>
                          </div>
                        )}
                      </div>
                    )}
                    {activeTab === 'plain' && (
                      <div className="tab-pane fade show active">
                        {/* Generate Plain Text Button */}
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleConvertFormat('plain_text')}
                            disabled={!generatedNote || convertStatus === 'loading'}
                          >
                            {convertStatus === 'loading' ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Generating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-sync-alt me-1"></i>
                                {generatedFormats.plain_text ? 'Regenerate Plain Text' : 'Generate Plain Text'}
                              </>
                            )}
                          </button>
                          {generatedFormats.plain_text && (
                            <CopyButton text={generatedFormats.plain_text} fieldId="generated-plain-text" />
                          )}
                          {convertError && (
                            <small className="text-danger ms-2">
                              Error: {convertError.error || 'Failed to generate plain text'}
                            </small>
                          )}
                        </div>

                        {generatedFormats.plain_text ? (
                          <pre className="border rounded p-3 bg-light">
                            {generatedFormats.plain_text}
                          </pre>
                        ) : (
                          <div className="text-center text-muted p-4 border rounded">
                            <i className="fas fa-file-alt mb-2" style={{ fontSize: '2rem' }}></i>
                            <p className="mb-0">Click &quot;Generate Plain Text&quot; to render this note</p>
                          </div>
                        )}
                      </div>
                    )}
                    {activeTab === 'rtf' && (
                      <div className="tab-pane fade show active">
                        {/* Generate RTF Button */}
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleConvertFormat('rtf')}
                            disabled={!generatedNote || convertStatus === 'loading'}
                          >
                            {convertStatus === 'loading' ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Generating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-sync-alt me-1"></i>
                                {generatedFormats.rtf ? 'Regenerate RTF' : 'Generate RTF'}
                              </>
                            )}
                          </button>
                          {generatedFormats.rtf && (
                            <CopyButton text={generatedFormats.rtf} fieldId="generated-rtf" />
                          )}
                          {convertError && (
                            <small className="text-danger ms-2">
                              Error: {convertError.error || 'Failed to generate RTF'}
                            </small>
                          )}
                        </div>

                        {generatedFormats.rtf ? (
                          <pre className="border rounded p-3 bg-light font-monospace" style={{ fontSize: '0.75rem' }}>
                            {generatedFormats.rtf}
                          </pre>
                        ) : (
                          <div className="text-center text-muted p-4 border rounded">
                            <i className="fas fa-file-word mb-2" style={{ fontSize: '2rem' }}></i>
                            <p className="mb-0">Click &quot;Generate RTF&quot; to render this note</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Comparison Section */}
                  {existingNotes.length > 0 && (
                    <div className="mt-4">
                      <h6>Compare with Existing Notes</h6>
                      <select className="form-select form-select-sm mb-2">
                        <option value="">Select a note to compare...</option>
                        {existingNotes.map((note, idx) => (
                          <option key={note.note_id || idx} value={note.note_id}>
                            {note.template_name || 'Unnamed Note'} - {new Date(note.created_at).toLocaleDateString()}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Runs Sidebar */}
      <SidebarPanel
        isOpen={showPipelineRunsPanel}
        onClose={() => setShowPipelineRunsPanel(false)}
        title="Pipeline Runs">
        <PipelineRunsPanel onLoadRun={handleLoadPipelineRun} />
      </SidebarPanel>
    </div>
  );
};

export default NoteGenerationSandbox;
